import React from 'react';
import { ChevronsUpDown, ArrowUpRight } from 'lucide-react';
import { cn } from '../../utils/cn';

export function Sidebar({ className, children, ...rest }) {
  return (
    <aside
      className={cn(
        'w-[260px] shrink-0 bg-ink text-cream flex flex-col',
        className,
      )}
      {...rest}
    >
      {children}
    </aside>
  );
}

function Brand({ name, kicker, className, children, action }) {
  return (
    <div className={cn('px-5 pt-7 pb-5', className)}>
      <div className="flex items-center justify-between">
        <div>
          {children || (
            <>
              <div className="text-2xl font-bold tracking-tight leading-none">
                {name}
                <span className="font-serif italic text-[1.1em] font-normal align-baseline">.</span>
              </div>
              {kicker && <div className="text-[10.5px] uppercase tracking-[0.2em] text-cream/55 mt-2">{kicker}</div>}
            </>
          )}
        </div>
        {action || <button className="text-cream/60 hover:text-cream"><ChevronsUpDown className="w-3.5 h-3.5" /></button>}
      </div>
    </div>
  );
}

function Nav({ className, children }) {
  return <nav className={cn('flex-1 overflow-y-auto px-3 space-y-0.5', className)}>{children}</nav>;
}

function Section({ label, className, children }) {
  return (
    <div className={cn(className)}>
      {label && <div className="px-3 pt-4 pb-1 text-[10.5px] uppercase tracking-[0.18em] text-cream/45 font-medium">{label}</div>}
      {children}
    </div>
  );
}

function NavItem({ icon, label, count, badge, isActive, as: Tag = 'a', className, ...rest }) {
  return (
    <Tag
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
        isActive ? 'bg-cream text-ink font-semibold' : 'text-cream/75 hover:bg-cream/10 hover:text-cream',
        className,
      )}
      {...rest}
    >
      {icon && <span className="[&_svg]:w-[15px] [&_svg]:h-[15px] shrink-0">{icon}</span>}
      <span className="flex-1 truncate">{label}</span>
      {count != null && <span className={cn('text-[11px] font-mono', isActive ? 'text-mute' : 'opacity-70')}>{count}</span>}
      {badge && <span className="shrink-0">{badge}</span>}
    </Tag>
  );
}

function Footer({ className, children }) {
  return <div className={cn('px-3 pb-3 mt-2 border-t border-cream/10 pt-3', className)}>{children}</div>;
}

/**
 * SignatureBlock — bloc cream sur fond ink avec chiffre serif filigrane
 * (signature visuelle de la piste 8). À placer dans le Footer ou au-dessus.
 *
 * Props :
 *   title       : ReactNode — titre signature (souvent en serif italique)
 *   description : ReactNode — citation / petite note
 *   ghost       : ReactNode — chiffre/texte filigrane en serif
 *   action      : ReactNode — bouton/lien en bas (ex. "Profil →")
 */
function SignatureBlock({ title, description, ghost, action, className }) {
  return (
    <div className={cn('mx-3 mb-3 p-4 rounded-lg bg-cream text-ink relative overflow-hidden', className)}>
      {ghost != null && ghost !== '' && (
        <div
          aria-hidden
          className="absolute -bottom-6 -right-6 font-serif italic text-[120px] leading-none opacity-10 select-none pointer-events-none"
        >
          {ghost}
        </div>
      )}
      <div className="relative">
        {title && (
          <div className="font-serif italic text-[16px] leading-tight">{title}</div>
        )}
        {description && (
          <div className="text-[11px] mt-2 text-ink/65 leading-relaxed">{description}</div>
        )}
        {action && (
          <div className="mt-3 text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5">
            {action}
          </div>
        )}
      </div>
    </div>
  );
}

Sidebar.Brand = Brand;
Sidebar.Nav = Nav;
Sidebar.Section = Section;
Sidebar.NavItem = NavItem;
Sidebar.Footer = Footer;
Sidebar.SignatureBlock = SignatureBlock;

export { ArrowUpRight as SignatureArrow };
export default Sidebar;
